<template>
  <div class="register-page">
    <div class="container-fluid h-100">
      <div class="row h-100">
        <!-- LEFT: Value / Guidance Panel (NO IMAGE) -->
        <div
          class="col-lg-5 d-none d-lg-flex  align-items-center justify-content-center register-left"
        >
          <div  class="register-left-inner text-white">
            <!-- Brand -->
            <div class="d-flex align-items-center gap-3 mb-5">
              <hr-payroll-mark variant="full" tone="dark" />
            </div>

            <!-- Headline -->
            <h1 class="display-6 fw-bold mb-3">
              Build your company workspace —
              <span class="text-white-75">then run everything</span> from one
              place.
            </h1>

            <p class=" text-white-75 mb-4">
              Set up once. Your HR flows into payroll, attendance supports
              timesheets, deals become invoices, and projects track delivery —
              beautifully connected.
            </p>

            <!-- Trust chips -->
            <div class="trust-grid mb-5">
              <div class="trust-chip">
                <i class="ti ti-shield-check"></i>
                Secure, role-based access
              </div>
              <div class="trust-chip">
                <i class="ti ti-file-certificate"></i>
                Compliance-ready workflows
              </div>
              <div class="trust-chip">
                <i class="ti ti-sparkles"></i>
                No credit card required
              </div>
              <div class="trust-chip">
                <i class="ti ti-report-analytics"></i>
                Real-time reporting
              </div>
            </div>

            <!-- Process hint -->
            <div class="process-card">
              <div class="d-flex align-items-start gap-3">
                <div class="process-icon">
                  <i class="ti ti-route"></i>
                </div>
                <div>
                  <div class="fw-bold mb-1">What happens after signup?</div>
                  <div class="text-white-75 small mb-3">
                    First you’ll verify your email, then choose or confirm your
                    plan before entering the company workspace.
                  </div>

                  <div class="process-steps">
                    <div class="step">
                      <span class="step-dot"></span>
                      Create your company account
                    </div>
                    <div class="step">
                      <span class="step-dot"></span>
                      Verify your admin email
                    </div>
                    <div class="step">
                      <span class="step-dot"></span>
                      Choose your plan & complete billing if needed
                    </div>
                    <div class="step">
                      <span class="step-dot"></span>
                      Invite your team & start operations
                    </div>
                  </div>

                  <div class="mt-3 d-flex flex-wrap gap-2">
                    <span class="pill">Company-scoped data</span>
                    <span class="pill">Approvals</span>
                    <span class="pill">Exports</span>
                    <span class="pill">Audit trails</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 text-white-50 small">
              <i class="ti ti-lock me-1"></i>
              Private by design · Controlled access · Built for growing teams
            </div>
          </div>
        </div>

        <!-- RIGHT: Registration Form -->
        <div
          class="col-lg-7 col-md-12 d-flex align-items-center justify-content-center"
        >
          <div class="register-form-wrapper w-100">
            <transition name="fade" appear>
              <form class="register-card" @submit.prevent="onSubmit">
                <!-- Top bar -->
                <div
                  class="d-flex align-items-center justify-content-between mb-3"
                >
                  <router-link to="/" class="back-link">
                    <i class="ti ti-arrow-left"></i>
                    <span class="ms-2">Back to site</span>
                  </router-link>

                  <span class="badge-lite">
                    <i class="ti ti-sparkles me-1"></i>
                    Start Free Trial
                  </span>
                </div>

                <!-- ✅ Plan summary pill (Option 3) -->
                <div v-if="selectedPlanId" class="plan-pill mb-4">
                  <div class="d-flex align-items-center gap-2">
                    <i class="ti ti-crown"></i>
                    <div class="flex-grow-1">
                      <div class="fw-bold">Plan selected</div>
                      <div class="small text-muted">
                        You’ll register under:
                        <span class="fw-semibold">{{ selectedPlanLabel }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Header -->
                <div class="text-center mb-4">
                  <div class="brand-icon-sm mx-auto mb-3">
                    <i class="ti ti-rocket"></i>
                  </div>
                  <h2 class="fw-bold mb-1">Create your company account</h2>
                  <p class="text-muted mb-0">
                    Get started in minutes — then invite your team and go live.
                  </p>
                </div>

                <!-- Company + workspace -->
                <div
                  class="company-workspace-block mb-4"
                  :class="{ 'company-workspace-block--tenancy': tenancyEnabled }"
                >
                  <div v-if="tenancyEnabled" class="company-workspace-heading">
                    <span class="company-workspace-heading-icon">
                      <i class="ti ti-building-community"></i>
                    </span>
                    <div>
                      <div class="fw-semibold">Your company workspace</div>
                      <div class="small text-muted">
                        Name your company and choose the URL your team will use
                        to sign in.
                      </div>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label class="form-label">Company name</label>
                    <div class="input-group input-group-lg ux-input">
                      <span class="input-group-text">
                        <i class="ti ti-building"></i>
                      </span>
                      <input
                        v-model="company_name"
                        type="text"
                        class="form-control"
                        placeholder="e.g. Example Technologies Ltd"
                        autocomplete="organization"
                        @input="syncSlugFromCompanyName"
                      />
                    </div>
                    <small
                      v-if="errors.company_name"
                      class="text-danger d-block mt-1"
                    >
                      {{ errors.company_name }}
                    </small>
                  </div>

                  <div v-if="tenancyEnabled" class="workspace-url-panel">
                    <label class="form-label mb-1">Workspace URL</label>
                    <p class="workspace-url-help">
                      Lowercase letters, numbers, and hyphens only. You can edit
                      this before creating your account.
                    </p>

                    <div
                      class="workspace-url-bar input-group input-group-lg ux-input"
                      :class="{
                        'workspace-url-bar--available': slugStatus === 'available',
                        'workspace-url-bar--taken':
                          slugStatus === 'taken' || Boolean(errors.slug),
                      }"
                    >
                      <span class="input-group-text workspace-url-prefix"
                        >https://</span
                      >
                      <input
                        v-model="slug"
                        type="text"
                        class="form-control workspace-url-slug"
                        placeholder="example-technologies"
                        autocomplete="off"
                        spellcheck="false"
                        @input="onSlugManualInput"
                      />
                      <span class="input-group-text workspace-url-suffix"
                        >.{{ tenantDomainSuffix }}</span
                      >
                    </div>

                    <div class="workspace-url-meta">
                      <div
                        v-if="workspacePreview"
                        class="workspace-url-preview"
                      >
                        <i class="ti ti-world"></i>
                        <span>{{ workspacePreview }}</span>
                      </div>

                      <div
                        v-if="slugStatus === 'available'"
                        class="workspace-url-status workspace-url-status--ok"
                      >
                        <i class="ti ti-circle-check"></i>
                        Available
                      </div>
                      <div
                        v-else-if="slugStatus === 'checking'"
                        class="workspace-url-status workspace-url-status--pending"
                      >
                        <i class="ti ti-loader-2"></i>
                        Checking…
                      </div>
                      <div
                        v-else-if="slugStatus === 'reserved'"
                        class="workspace-url-status workspace-url-status--bad"
                      >
                        <i class="ti ti-alert-circle"></i>
                        Reserved URL
                      </div>
                      <div
                        v-else-if="slugStatus === 'taken'"
                        class="workspace-url-status workspace-url-status--bad"
                      >
                        <i class="ti ti-circle-x"></i>
                        Already taken
                      </div>
                      <div
                        v-else-if="slugStatus === 'invalid'"
                        class="workspace-url-status workspace-url-status--bad"
                      >
                        <i class="ti ti-alert-circle"></i>
                        Invalid URL
                      </div>
                    </div>

                    <small
                      v-if="errors.slug"
                      class="text-danger d-block mt-2"
                    >
                      {{ errors.slug }}
                    </small>
                  </div>
                </div>

                <!-- Work email -->
                <div class="mb-3">
                  <label class="form-label">Work email</label>
                  <div class="input-group input-group-lg ux-input">
                    <span class="input-group-text">
                      <i class="ti ti-mail"></i>
                    </span>
                    <input
                      v-model="email"
                      type="email"
                      class="form-control"
                      placeholder="you@company.com"
                      autocomplete="email"
                    />
                  </div>
                  <small v-if="errors.email" class="text-danger d-block mt-1">
                    {{ errors.email }}
                  </small>
                </div>

                <!-- Names -->
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">First name</label>
                    <div class="input-group input-group-lg ux-input">
                      <span class="input-group-text">
                        <i class="ti ti-user"></i>
                      </span>
                      <input
                        v-model="first_name"
                        type="text"
                        class="form-control"
                        placeholder="e.g. Jane"
                        autocomplete="given-name"
                      />
                    </div>
                    <small
                      v-if="errors.first_name"
                      class="text-danger d-block mt-1"
                    >
                      {{ errors.first_name }}
                    </small>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Last name</label>
                    <div class="input-group input-group-lg ux-input">
                      <span class="input-group-text">
                        <i class="ti ti-user"></i>
                      </span>
                      <input
                        v-model="last_name"
                        type="text"
                        class="form-control"
                        placeholder="e.g. Doe"
                        autocomplete="family-name"
                      />
                    </div>
                    <small
                      v-if="errors.last_name"
                      class="text-danger d-block mt-1"
                    >
                      {{ errors.last_name }}
                    </small>
                  </div>
                </div>

                <!-- Password -->
                <div class="mb-3">
                  <label class="form-label">Password</label>
                  <div class="input-group input-group-lg ux-input">
                    <span class="input-group-text">
                      <i class="ti ti-key"></i>
                    </span>
                    <input
                      v-model="password"
                      :type="showPassword ? 'text' : 'password'"
                      class="form-control"
                      placeholder="Minimum 8 characters"
                      autocomplete="new-password"
                    />
                    <button
                      type="button"
                      class="input-group-text btn-toggle"
                      @click="toggleShow"
                      :aria-label="
                        showPassword ? 'Hide password' : 'Show password'
                      "
                    >
                      <i
                        class="ti"
                        :class="showPassword ? 'ti-eye' : 'ti-eye-off'"
                      ></i>
                    </button>
                  </div>
                  <small
                    v-if="errors.password"
                    class="text-danger d-block mt-1"
                  >
                    {{ errors.password }}
                  </small>

                  <!-- ✅ Password strength meter + checklist (Option 1) -->
                  <div class="strength mt-3">
                    <div
                      class="d-flex align-items-center justify-content-between mb-2"
                    >
                      <div class="small fw-semibold text-muted">
                        Password strength
                      </div>
                      <div class="small fw-bold" :class="strengthLabelClass">
                        {{ strengthLabel }}
                      </div>
                    </div>

                    <div
                      class="strength-bars"
                      role="progressbar"
                      :aria-valuenow="strengthScore"
                      aria-valuemin="0"
                      aria-valuemax="4"
                    >
                      <span
                        v-for="n in 4"
                        :key="n"
                        class="bar"
                        :class="{ on: strengthScore >= n }"
                      />
                    </div>

                    <div class="reqs mt-2">
                      <div class="req" :class="{ ok: reqs.len }">
                        <i
                          class="ti"
                          :class="reqs.len ? 'ti-check' : 'ti-circle-dashed'"
                        ></i>
                        At least 8 characters
                      </div>
                      <div class="req" :class="{ ok: reqs.upper }">
                        <i
                          class="ti"
                          :class="reqs.upper ? 'ti-check' : 'ti-circle-dashed'"
                        ></i>
                        One uppercase letter
                      </div>
                      <div class="req" :class="{ ok: reqs.lower }">
                        <i
                          class="ti"
                          :class="reqs.lower ? 'ti-check' : 'ti-circle-dashed'"
                        ></i>
                        One lowercase letter
                      </div>
                      <div class="req" :class="{ ok: reqs.num }">
                        <i
                          class="ti"
                          :class="reqs.num ? 'ti-check' : 'ti-circle-dashed'"
                        ></i>
                        One number
                      </div>
                      <div class="req" :class="{ ok: reqs.sym }">
                        <i
                          class="ti"
                          :class="reqs.sym ? 'ti-check' : 'ti-circle-dashed'"
                        ></i>
                        One symbol (e.g. !@#)
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Confirm -->
                <div class="mb-3">
                  <label class="form-label">Confirm password</label>
                  <div class="input-group input-group-lg ux-input">
                    <span class="input-group-text">
                      <i class="ti ti-key"></i>
                    </span>
                    <input
                      v-model="password_confirmation"
                      :type="showPassword1 ? 'text' : 'password'"
                      class="form-control"
                      placeholder="Repeat password"
                      autocomplete="new-password"
                    />
                    <button
                      type="button"
                      class="input-group-text btn-toggle"
                      @click="toggleShow1"
                      :aria-label="
                        showPassword1 ? 'Hide password' : 'Show password'
                      "
                    >
                      <i
                        class="ti"
                        :class="showPassword1 ? 'ti-eye' : 'ti-eye-off'"
                      ></i>
                    </button>
                  </div>
                  <small
                    v-if="errors.password_confirmation"
                    class="text-danger d-block mt-1"
                  >
                    {{ errors.password_confirmation }}
                  </small>
                </div>

                <!-- Terms -->
                <div class="mb-3">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      id="agree"
                      type="checkbox"
                      v-model="agree"
                    />
                    <label for="agree" class="form-check-label">
                      I agree to the
                      <router-link
                        to="/terms-condition"
                        target="_blank"
                        class="text-primary fw-semibold text-decoration-none"
                      >
                        Terms & Conditions
                      </router-link>
                      and
                      <router-link
                        to="/privacy-policy"
                        target="_blank"
                        class="text-primary fw-semibold text-decoration-none"
                      >
                        Privacy Policy
                      </router-link>
                    </label>
                  </div>
                  <small v-if="errors.agree" class="text-danger d-block mt-1">
                    {{ errors.agree }}
                  </small>
                </div>

                <!-- ✅ Submit disabled until valid (Option 2) -->
                <button
                  type="submit"
                  class="btn btn-primary btn-lg w-100 btn-cta"
                  :disabled="loading || !meta.valid"
                  :aria-busy="loading"
                >
                  <span
                    v-if="loading"
                    class="d-inline-flex align-items-center justify-content-center"
                  >
                    <span
                      class="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Creating your workspace…
                  </span>
                  <span
                    v-else
                    class="d-inline-flex align-items-center justify-content-center"
                  >
                    Create company & continue
                    <i class="ti ti-arrow-right ms-2"></i>
                  </span>
                </button>

                <!-- Small UX hint when invalid -->
                <div v-if="!meta.valid" class="invalid-hint mt-2">
                  <i class="ti ti-alert-circle me-2"></i>
                  Complete the required fields to continue.
                </div>

                <!-- Helper hint -->
                <div class="hint mt-3">
                  <i class="ti ti-info-circle me-2"></i>
                  Tip: Use your main company email so your team can join easily
                  later.
                </div>

                <!-- Divider -->
                <div class="divider my-4">
                  <span>Already have an account?</span>
                </div>

                <!-- Sign in -->
                <button
                  type="button"
                  class="btn btn-outline-dark btn-lg w-100"
                  :disabled="loading"
                  @click="openWorkspaceSignIn"
                >
                  Sign in instead
                </button>

                <!-- Footer -->
                <p class="text-center text-muted small mt-4 mb-0">
                  2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll
                </p>
              </form>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <!-- Background accents -->
    <div class="blob blob-1"></div>
    <div class="blob blob-2"></div>

    <workspace-sign-in-modal
      ref="workspaceSignInModal"
      modal-id="register_workspace_sign_in"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import HrPayrollMark from "@/components/branding/hr-payroll-mark.vue";
import WorkspaceSignInModal from "@/components/auth/workspace-sign-in-modal.vue";
import { useRoute, useRouter } from "vue-router";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { useRegisterForm } from "@/composables/useRegisterForm";
import publicPlanService from "@/services/public-plan.service";
import {
  isTenancyFrontendEnabled,
  slugifyWorkspaceName,
  tenantDomainLabel,
  workspacePreviewUrl,
} from "@/utils/hostname";
import axios from "axios";

// composable
const { submit, loading } = useRegisterForm();
const router = useRouter();
const workspaceSignInModal = ref(null);

function openWorkspaceSignIn() {
  if (loading.value) return;
  if (!isTenancyFrontendEnabled()) {
    router.push("/login");
    return;
  }
  workspaceSignInModal.value?.open();
}

// validation schema (UNCHANGED)
const schema = yup.object({
  company_name: yup.string().required("Company name is required"),
  slug: isTenancyFrontendEnabled()
    ? yup
        .string()
        .required("Workspace URL is required")
        .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers, and hyphens")
    : yup.string().nullable(),
  email: yup.string().email("Invalid email").required("Email is required"),
  first_name: yup.string().required("First name is required"),
  last_name: yup.string().required("Last name is required"),
  password: yup
    .string()
    .min(8, "Min 8 characters")
    .required("Password is required"),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Confirm your password"),
  agree: yup
    .boolean()
    .oneOf([true], "You must agree to the Terms & Privacy to continue"),
});

// ✅ meta for validity (Option 2)
const { handleSubmit, errors, meta, setFieldValue } = useForm({
  validationSchema: schema,
  initialValues: {
    company_name: "",
    slug: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    password_confirmation: "",
    agree: false,
  },
});

const { value: company_name } = useField("company_name");
const { value: slug } = useField("slug");
const { value: email } = useField("email");
const { value: first_name } = useField("first_name");
const { value: last_name } = useField("last_name");
const { value: password } = useField("password");
const { value: password_confirmation } = useField("password_confirmation");
const { value: agree } = useField("agree", yup.boolean().default(false));

// password toggles
const showPassword = ref(false);
const showPassword1 = ref(false);
const toggleShow = () => (showPassword.value = !showPassword.value);
const toggleShow1 = () => (showPassword1.value = !showPassword1.value);

// ✅ Plan pill from query (Option 3)
const route = useRoute();
const availablePlans = ref([]);
const selectedPlanId = computed(() => {
  const v = route.query?.plan_id;
  return v ? String(v) : "";
});
const selectedPlan = computed(() =>
  availablePlans.value.find((plan) => String(plan.id) === selectedPlanId.value) ||
  null
);
const selectedPlanLabel = computed(
  () => selectedPlan.value?.name || `Plan #${selectedPlanId.value}`
);

// body class management
const updateBodyClass = () => {
  document.body.classList.add("bg-light");
};
onMounted(async () => {
  updateBodyClass();

  if (!selectedPlanId.value) return;

  try {
    const res = await publicPlanService.getAll();
    availablePlans.value = Array.isArray(res?.data) ? res.data : [];
  } catch (e) {
    availablePlans.value = [];
  }
});
const tenancyEnabled = computed(() => isTenancyFrontendEnabled());
const tenantDomainSuffix = computed(() => tenantDomainLabel());
const slugManuallyEdited = ref(false);
let autoUpdatingSlug = false;
let slugLookupTimer = null;
let slugLookupController = null;

const syncSlugFromCompanyName = () => {
  if (!tenancyEnabled.value || slugManuallyEdited.value) return;
  autoUpdatingSlug = true;
  setFieldValue("slug", slugifyWorkspaceName(company_name.value));
  autoUpdatingSlug = false;
};

watch(() => company_name.value, syncSlugFromCompanyName);

const onSlugManualInput = () => {
  if (autoUpdatingSlug) return;
  slugManuallyEdited.value = true;
};

const workspacePreview = computed(() => workspacePreviewUrl(slug.value));
const slugStatus = ref(null);

watch(
  () => slug.value,
  (value) => {
    slugStatus.value = null;

    if (slugLookupTimer) {
      clearTimeout(slugLookupTimer);
      slugLookupTimer = null;
    }
    if (slugLookupController) {
      slugLookupController.abort();
      slugLookupController = null;
    }

    if (!isTenancyFrontendEnabled() || !value) return;

    slugLookupTimer = setTimeout(async () => {
      const requestSlug = slugifyWorkspaceName(value);
      if (!requestSlug) return;

      slugLookupController = new AbortController();

      try {
        slugStatus.value = "checking";
        const apiBase =
          process.env.VUE_APP_API_URL || "http://127.0.0.1:8000/api/";
        const res = await axios.get(`${apiBase}central/tenant-lookup`, {
          params: { slug: requestSlug },
          signal: slugLookupController.signal,
        });

        if (slugifyWorkspaceName(slug.value) !== requestSlug) return;

        if (res.data?.available === true) {
          slugStatus.value = "available";
          return;
        }

        const message = String(res.data?.message || "").toLowerCase();
        slugStatus.value = message.includes("reserved") ? "reserved" : "taken";
      } catch (e) {
        if (e?.code === "ERR_CANCELED") return;
        if (slugifyWorkspaceName(slug.value) !== requestSlug) return;
        slugStatus.value = "invalid";
      } finally {
        slugLookupController = null;
      }
    }, 350);
  }
);

watch(() => route.fullPath, updateBodyClass);

// ✅ Password strength (Option 1)
const reqs = computed(() => {
  const p = String(password.value || "");
  return {
    len: p.length >= 8,
    upper: /[A-Z]/.test(p),
    lower: /[a-z]/.test(p),
    num: /[0-9]/.test(p),
    sym: /[^A-Za-z0-9]/.test(p),
  };
});

const strengthScore = computed(() => {
  const r = reqs.value;
  // score 0..4 based on how many key requirements are met
  const hits = [r.len, r.upper, r.lower, r.num, r.sym].filter(Boolean).length;

  // map 0..5 into 0..4 (more forgiving)
  if (hits <= 1) return 1;
  if (hits === 2) return 2;
  if (hits === 3) return 3;
  return 4;
});

const strengthLabel = computed(() => {
  const p = String(password.value || "");
  if (!p) return "—";
  if (strengthScore.value <= 1) return "Weak";
  if (strengthScore.value === 2) return "Fair";
  if (strengthScore.value === 3) return "Good";
  return "Strong";
});

const strengthLabelClass = computed(() => {
  if (strengthLabel.value === "Weak") return "text-danger";
  if (strengthLabel.value === "Fair") return "text-warning";
  if (strengthLabel.value === "Good") return "text-primary";
  if (strengthLabel.value === "Strong") return "text-success";
  return "text-muted";
});

// submit
const onSubmit = handleSubmit((values) => submit(values));
</script>

<style scoped>
/* Base */
.register-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: radial-gradient(
      900px 380px at 15% 0%,
      rgba(59, 130, 246, 0.16),
      transparent 60%
    ),
    radial-gradient(
      900px 380px at 85% 10%,
      rgba(99, 102, 241, 0.14),
      transparent 60%
    ),
    #f8fafc;
}

/* Background blobs */
.blob {
  position: absolute;
  filter: blur(45px);
  opacity: 0.35;
  pointer-events: none;
  z-index: 0;
}
.blob-1 {
  width: 420px;
  height: 420px;
  left: -160px;
  bottom: -160px;
  background: rgba(59, 130, 246, 0.55);
  border-radius: 999px;
}
.blob-2 {
  width: 520px;
  height: 520px;
  right: -220px;
  top: -220px;
  background: rgba(99, 102, 241, 0.55);
  border-radius: 999px;
}

/* ensure content above blobs */
.container-fluid,
.row {
  position: relative;
  z-index: 1;
}

.text-white-75 {
  color: rgba(255, 255, 255, 0.78);
}

/* Left panel */
.register-left {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  padding: 64px;
}
.register-left-inner {
  max-width: 540px;
  transform: translateY(-180px);
}
.brand-icon {
  width: 56px;
  height: 56px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}

/* Trust chips */
.trust-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.trust-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
  font-weight: 650;
}
.trust-chip i {
  color: rgba(34, 197, 94, 0.95);
}

/* Process card */
.process-card {
  border-radius: 20px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
}
.process-icon {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
}
.process-steps {
  display: grid;
  gap: 10px;
}
.step {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.88);
  font-weight: 600;
}
.step-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.95);
  box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.16);
}

/* Pills */
.pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.82rem;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.85);
}

/* Right panel */
.register-form-wrapper {
  max-width: 700px;
  padding: 24px;
}
.register-card {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(2, 6, 23, 0.06);
  padding: 34px;
  border-radius: 22px;
  box-shadow: 0 28px 70px rgba(2, 6, 23, 0.12);
  backdrop-filter: blur(10px);
}

/* Top actions */
.back-link {
  display: inline-flex;
  align-items: center;
  color: #334155;
  text-decoration: none;
  font-weight: 650;
}
.back-link:hover {
  text-decoration: underline;
}
.badge-lite {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.1);
  border: 1px solid rgba(37, 99, 235, 0.18);
  color: #1d4ed8;
  font-weight: 750;
  font-size: 0.84rem;
}

.brand-icon-sm {
  width: 52px;
  height: 52px;
  border-radius: 18px;
  background: rgba(37, 99, 235, 0.12);
  border: 1px solid rgba(37, 99, 235, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  font-size: 22px;
}

/* ✅ Plan pill */
.plan-pill {
  border-radius: 18px;
  padding: 12px 14px;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.18);
}
.plan-pill i {
  color: #f59e0b;
  font-size: 18px;
}

/* Company + workspace block */
.company-workspace-block--tenancy {
  padding: 18px;
  border-radius: 18px;
  background: linear-gradient(
    180deg,
    rgba(37, 99, 235, 0.05) 0%,
    rgba(255, 255, 255, 0.72) 100%
  );
  border: 1px solid rgba(37, 99, 235, 0.12);
}

.company-workspace-heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.company-workspace-heading-icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(37, 99, 235, 0.12);
  border: 1px solid rgba(37, 99, 235, 0.16);
  color: #2563eb;
  font-size: 20px;
  flex-shrink: 0;
}

.workspace-url-panel {
  padding-top: 4px;
}

.workspace-url-help {
  margin: 0 0 10px;
  font-size: 0.84rem;
  color: #64748b;
}

.workspace-url-bar .workspace-url-prefix,
.workspace-url-bar .workspace-url-suffix {
  color: #64748b;
  font-size: 0.92rem;
  white-space: nowrap;
}

.workspace-url-slug {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
}

.workspace-url-bar--available:focus-within .input-group-text,
.workspace-url-bar--available:focus-within .form-control {
  border-color: rgba(22, 163, 74, 0.55);
  box-shadow: 0 0 0 0.2rem rgba(22, 163, 74, 0.12);
}

.workspace-url-bar--taken:focus-within .input-group-text,
.workspace-url-bar--taken:focus-within .form-control {
  border-color: rgba(220, 38, 38, 0.55);
  box-shadow: 0 0 0 0.2rem rgba(220, 38, 38, 0.12);
}

.workspace-url-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.workspace-url-preview {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.08);
  color: #334155;
  font-size: 0.84rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
}

.workspace-url-preview i {
  color: #2563eb;
}

.workspace-url-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.84rem;
  font-weight: 650;
}

.workspace-url-status--ok {
  color: #15803d;
}

.workspace-url-status--pending {
  color: #64748b;
}

.workspace-url-status--bad {
  color: #dc2626;
}

/* Inputs */
.ux-input .input-group-text {
  background: #fff;
  border-color: rgba(2, 6, 23, 0.12);
}
.ux-input .form-control {
  border-color: rgba(2, 6, 23, 0.12);
}
.ux-input:focus-within .input-group-text,
.ux-input:focus-within .form-control {
  border-color: rgba(37, 99, 235, 0.55);
  box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.12);
}
.btn-toggle {
  cursor: pointer;
}
.btn-toggle:hover {
  background: rgba(2, 6, 23, 0.03);
}

/* CTA */
.btn-cta {
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(37, 99, 235, 0.25);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.btn-cta:hover:enabled {
  transform: translateY(-1px);
  box-shadow: 0 18px 46px rgba(37, 99, 235, 0.28);
}
.btn-cta:disabled {
  opacity: 0.7;
  box-shadow: none;
}

/* invalid hint */
.invalid-hint {
  display: flex;
  align-items: center;
  color: #64748b;
  font-size: 0.92rem;
}

/* Hint */
.hint {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(2, 6, 23, 0.02);
  border: 1px solid rgba(2, 6, 23, 0.06);
  color: #334155;
  font-size: 0.92rem;
}

/* Divider */
.divider {
  position: relative;
  text-align: center;
}
.divider span {
  background: rgba(255, 255, 255, 0.92);
  padding: 0 12px;
  font-size: 13px;
  color: #6b7280;
  font-weight: 650;
}
.divider::before {
  content: "";
  position: absolute;
  inset: 50% 0 auto 0;
  height: 1px;
  background: rgba(2, 6, 23, 0.1);
  z-index: -1;
}

/* ✅ Password strength styles */
.strength {
  border-radius: 16px;
  padding: 12px;
  background: rgba(2, 6, 23, 0.02);
  border: 1px solid rgba(2, 6, 23, 0.06);
}
.strength-bars {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.strength-bars .bar {
  height: 8px;
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.1);
  transition: transform 0.15s ease, background 0.15s ease;
}
.strength-bars .bar.on {
  background: rgba(37, 99, 235, 0.75);
  transform: translateY(-1px);
}
.reqs {
  display: grid;
  gap: 8px;
}
.req {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.92rem;
  color: #64748b;
}
.req i {
  font-size: 16px;
}
.req.ok {
  color: #15803d;
}
.req.ok i {
  color: #22c55e;
}

/* Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.45s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 576px) {
  .register-card {
    padding: 26px;
    border-radius: 18px;
  }
}
</style>


